import { COMMON_TAB_IDS, COMMON_TABS_PROPS } from 'features/inventory-profiles/constants';
import strings from 'strings';
import FileManager from 'UI/components/organisms/FileManager';
import {
  formatResponseActivityTable,
  formatResponseJobOrderActivityTable
} from 'UI/components/organisms/inventoryProfiles/ActivityTab/activity.utils';
import ActivityNoteTabLayout from 'UI/components/organisms/inventoryProfiles/ActivityTab/ActivityTab';
import { ChangeLogsTab } from 'UI/components/organisms/inventoryProfiles/ChangeLogsTab';
import NotesTab from 'UI/components/organisms/inventoryProfiles/NotesTab/NotesTab';
import SearchProjectsTab from 'UI/components/organisms/inventoryProfiles/SearchProjectsTab';
import {
  columns as searchProjectColumns,
  virtualProps as searchProjectVirtualProps
} from 'UI/components/organisms/inventoryProfiles/SearchProjectsTab/columns';
import { Endpoints } from 'UI/constants/endpoints';
import { EntityType } from 'UI/constants/entityTypes';
import { getProfileFileDraggableProps } from 'UI/utils';

const COPIES = strings.inventoryProfiles;

const JOB_ORDER_ACTIVITY_INCLUDES = 'activityLogType,user';
const ACTIVITY_INCLUDES = 'activityLogType,user,outcomes';

/**
 * The five tabs every profile ends with, reusing the V1 sections and the default props of
 * COMMON_TABS_PROPS (preference keys, API versions, file module), copied from V1 commons.jsx.
 * Each tab gets exactly the props its V1 component reads: the V1 `initialColumns`, `tabKey`,
 * `type` and `extraRenderers` of Activity and Notes, and the `endpoint`, `initialColumns` and
 * `search` of Changelog, are not read by those components and are left out ("Props muertas en V1").
 * Tab ids come from COMMON_TAB_IDS. build<Entity>Tabs spreads the result at the end of its own `tabs` array.
 * @param {Object} params
 * @param {number|string} params.profileId `profile.id` of the loaded profile
 * @param {Object} params.entity ENTITIES.<entity>. Its `id` is the V1 entity type
 *   (<X>Entity.id, equal to EntityType.*): the module of Activity and Notes, and how Job Order is detected
 * @param {string} params.itemName `formatted<Entity>.header.title`: the Notes form shows it
 * @param {() => void} params.onActivityComplete `refresh` of the container: a saved note reloads the
 *   profile silently, so the last activity of the header changes
 * @param {{ id: string, tabProps: Object }} [params.searchProjectsTab] `searchProjectTabPropsDefinition` of
 *   useSearchProjectInProfiles (V1). Omitted for Job Order: V1 hides the tab
 * @returns {Array<{ id: string, label: string, content: React.ReactNode, visible: boolean }>}
 */
export const getCommonTabs = ({
  entity,
  itemName,
  onActivityComplete,
  profileId,
  searchProjectsTab
}) => {
  const { defaultTabsProps, fileManagerModule } = COMMON_TABS_PROPS[entity.type];
  const endpoint = `${entity.endpoint}/${profileId}`;
  const isJobOrder = entity.id === EntityType.Joborder;

  const activityDataTable = {
    endpoint: `${endpoint}/${Endpoints.ActivityLogs}`,
    apiVersion: 1,
    includes: isJobOrder ? JOB_ORDER_ACTIVITY_INCLUDES : ACTIVITY_INCLUDES,
    formatResponse: isJobOrder ? formatResponseJobOrderActivityTable : formatResponseActivityTable,
    ...defaultTabsProps.activityLogProps
  };
  const notesDataTable = {
    endpoint: `${endpoint}/${Endpoints.Notes}`,
    apiVersion: 1,
    includes: 'user',
    ...defaultTabsProps.notesProps
  };
  const searchProjectsDataTable = {
    endpoint: `${endpoint}/${Endpoints.SearchProjects}`,
    virtualProps: searchProjectVirtualProps
  };

  const { copies, onOpenAddToSearchProjectForm, onRemoveProfileFromSearchProject, shouldRefresh } =
    searchProjectsTab?.tabProps ?? {};
  const changeLogDataTable = {
    endpoint: `${endpoint}/${Endpoints.ChangeLogs}`,
    includes: 'vCreatedBy'
  };

  return [
    {
      id: COMMON_TAB_IDS.SearchProjects,
      label: COPIES.common.tabs.searchProjects,
      visible: Boolean(searchProjectsTab),
      content: (
        <SearchProjectsTab
          copies={copies}
          DataTableProps={searchProjectsDataTable}
          endpoint={endpoint}
          initialColumns={searchProjectColumns}
          onOpenAddToSearchProjectForm={onOpenAddToSearchProjectForm}
          onRemoveProfileFromSearchProject={onRemoveProfileFromSearchProject}
          profileId={profileId}
          shouldRefresh={shouldRefresh}
          tablePreferencesKey={defaultTabsProps.searchProjectsProps?.tablePreferencesKey}
          tabKey={COMMON_TAB_IDS.SearchProjects}
        />
      )
    },
    {
      id: COMMON_TAB_IDS.ActivityLogs,
      label: COPIES.common.tabs.activity,
      visible: true,
      content: (
        <ActivityNoteTabLayout
          DataTableProps={activityDataTable}
          endpoint={endpoint}
          profileId={profileId}
          profileModule={entity.id}
          tablePreferencesKey={defaultTabsProps.activityPageKey}
        />
      )
    },
    {
      id: COMMON_TAB_IDS.Notes,
      label: COPIES.common.tabs.notes,
      visible: true,
      content: (
        <NotesTab
          DataTableProps={notesDataTable}
          endpoint={endpoint}
          itemName={itemName}
          onActivityComplete={onActivityComplete}
          profileId={profileId}
          profileModule={entity.id}
          tablePreferencesKey={defaultTabsProps.notesPageKey}
        />
      )
    },
    {
      id: COMMON_TAB_IDS.Attachments,
      label: COPIES.common.tabs.attachments,
      visible: true,
      content: (
        <FileManager
          FileDraggableProps={getProfileFileDraggableProps()}
          filesEndpoint={`${endpoint}/${Endpoints.Files}`}
          layoutInColumns
          module={fileManagerModule}
          readOnly={false}
          showFileSize
          showItemDate
          showItemMenu
          viewItemOnClick
        />
      )
    },
    {
      id: COMMON_TAB_IDS.ChangeLogs,
      label: COPIES.sections.tabs.changeLogs.sectionName,
      visible: true,
      content: (
        <ChangeLogsTab
          DataTableProps={changeLogDataTable}
          entityId={profileId}
          profileModule={entity.title}
          tablePreferencesKey={defaultTabsProps.changeLogProps.tablePreferencesKey}
        />
      )
    }
  ];
};
